/**
 * Determines whether pricing can be refreshed for a flight or room option.
 *
 * Checks:
 * 1. The type must be 'flight' or 'room'
 * 2. The travel date must be in the future
 * 3. All itinerary items referencing this option are in 'planned' status (or have no booking)
 */

import type { FlightOptionModel } from '#models/FlightOptionModel.js';
import type { ItineraryItem } from '#models/ItineraryItemModel.js';
import type { RoomOptionModel } from '#models/RoomOptionModel.js';
import type { ShortlistItemType } from '@elara/domain/models/schemas/shortlist-schemas';

/** Plain function for use outside of React components (e.g. inside useMemo). */
export function canRefreshPricing(
  type: ShortlistItemType | string,
  model: FlightOptionModel | RoomOptionModel | undefined,
  itineraryItems: ItineraryItem[],
): boolean {
  if (type !== 'flight' && type !== 'room') return false;
  if (!model) return false;
  if (model.isTravelDatePassed) return false;

  const referenceId = model.id;
  if (!referenceId) return false;

  // Block refresh if any itinerary item referencing this option has a non-planned booking status.
  // Use an allowlist (only 'planned' allows refresh) rather than a blocklist, so new statuses
  // are blocked by default.
  const matchingItems = itineraryItems.filter((item) => item.type === type && item.referenceId === referenceId);
  if (matchingItems.length === 0) return true;
  return matchingItems.every((item) => !item.booking?.status || item.booking.status === 'planned');
}
